// ============================================================
// PAINEL REVELAR TALENTOS — App.js
//
// Esqueleto do painel: autenticação, cabeçalho, barra lateral e
// roteamento. Toda a lógica de negócio, telas e componentes vivem em
// módulos próprios (Fase 1, Parte B):
//   firebase.js      — config, db/auth/functions, acesso ao Firestore
//   calculo.js       — regra de negócio da frequência
//   tema.js          — cores, MESES, TIPOS_DISPENSA
//   componentes/     — Card, Btn, Input, Sel, StatusBox, RodapeDynamon
//   excel.js         — geração do relatório Excel
//   secoes/          — SecaoRelatorio, SecaoDatas, SecaoAtletas, Resumo,
//                      SecaoNucleos (só admin), SecaoTecnico, SecaoComparativo,
//                      SecaoDesempenho, SecaoConfiguracoes
//   telas/Auth.js    — TelaCarregando, TelaLogin, ModalAlterarSenha
//
// Layout reformulado (mockup do Diego): logo fixa no cabeçalho, menu de
// perfil (com Alterar senha / Sair) no canto superior direito, navegação
// principal na barra lateral esquerda, título+descrição de cada página no
// início da área de conteúdo, rodapé sempre ao final da página inteira
// (não colado embaixo do último card).
// ============================================================

import { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db, classificarAcesso, carregarNucleos, contarSolicitacoesPendentes, carregarDecisoesParaCiencia } from "./firebase";
import { AZUL, CINZA_ESC, TEXTO, BRANCO, PERIGO, ALERTA_CL, ALERTA, MENU, ACAO, FONTE, TIPO_PAGINA, BORDA, RAIO } from "./tema";
import { Btn, StatusBox, Icone } from "./componentes";
import { TelaCarregando, TelaLogin, ModalAlterarSenha } from "./telas/Auth";
import { SecaoRelatorio, SecaoDatas, SecaoAtletas, Resumo, SecaoNucleos, SecaoComparativo, SecaoDesempenho, SecaoTreinosExternos } from "./secoes";
import SecaoAtleta from "./secoes/SecaoAtleta";
import { PortaDaAvaliacao } from "./secoes/SecaoAvaliacaoCiclo";
import SecaoAvaliacaoCoord from "./secoes/SecaoAvaliacaoCoord";
import SecaoPlanilhaTreino from "./secoes/SecaoPlanilhaTreino";
import SecaoAvaliacaoFisica from "./secoes/SecaoAvaliacaoFisica";
import SecaoAvaliacaoTecnica from "./secoes/SecaoAvaliacaoTecnica";
import SecaoTestesFisicos from "./secoes/SecaoTestesFisicos";
import SecaoFichaAtleta from "./secoes/SecaoFichaAtleta";
import SecaoResultados from "./secoes/SecaoResultados";
import SecaoRanking from "./secoes/SecaoRanking";
import TelaTecnico from "./secoes/TelaTecnico";
import { LINK_APP } from "./componentes/CardDownloadApp";
import TelaPreparador from "./secoes/TelaPreparador";
import SecaoPresencaFisica from "./secoes/SecaoPresencaFisica";
import SecaoSolicitacoes from "./secoes/SecaoSolicitacoes";
import SecaoCadastroFacial from "./secoes/SecaoCadastroFacial";
import SecaoCadastroAtletas from "./secoes/SecaoCadastroAtletas";
import SecaoPlanejamentoCoord from "./secoes/SecaoPlanejamentoCoord";
import SecaoInicio from "./secoes/SecaoInicio";
import SecaoFichasNucleo from "./secoes/SecaoFichasNucleo";
import SecaoPainelGeral from "./secoes/SecaoPainelGeral";
import FichaNucleo, { dadosDoLinkDaFicha } from "./telas/FichaNucleo";
import useIsMobile from "./useIsMobile";
import { NucleosProvider, useNomeNucleo } from "./NucleosContexto";



// Iniciais pro círculo do menu de perfil (ex: "Diego Lynamon" → "DL").
function iniciaisDoNome(nome) {
  const partes = (nome || "").trim().split(" ").filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0][0].toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

// Título, ícone, cor e descrição de cada página — usados tanto na barra
// lateral quanto no cabeçalho de conteúdo (fonte única, sem duplicar o
// título dentro de cada Secao*.js). O selo "Em breve" mora só aqui, nunca
// repetido na barra lateral.
// `icone` é o nome de uma forma de componentes/Icone.js, não mais um
// emoji: emoji muda de desenho conforme o sistema, não aceita a cor da
// tela e nunca alinha com o texto ao lado.
//
// `modulo` é o primeiro degrau da trilha no alto da página ("Gestão ›
// Usuários"). Sem ele, quem chega por um atalho não sabe em que parte do
// sistema está.
const METADADOS_ABA = {
  inicio: { titulo: "Início", modulo: null, icone: "casa", iconBg: "#DBEAFE", iconColor: "#2563EB", desc: "Como está o núcleo hoje e o que espera decisão da coordenação." },
  nucleos: { titulo: "Núcleos", modulo: "Gestão", icone: "predio", iconBg: "#D1FAE5", iconColor: "#059669", desc: "Gerencie os núcleos, suas equipes e os atletas vinculados ao programa." },
  painel_geral: { titulo: "Painel geral", modulo: "Administração", icone: "barras", iconBg: "#EAF0FA", iconColor: "#3055A5", desc: "Uma linha por atleta, com a última de cada medida: avaliação física, técnica, testes, escore global e ranking. E quem ainda falta medir." },
  fichas: { titulo: "Fichas de cadastro", modulo: "Administração", icone: "prancheta", iconBg: "#EAF0FA", iconColor: "#3055A5", desc: "Abra a ficha de um núcleo novo, mande o link para a coordenação geral e para a técnica, confira o que chegou e crie o núcleo." },
  atletas: { titulo: "Gerenciar usuários", modulo: "Gestão", icone: "usuarios", iconBg: "#DBEAFE", iconColor: "#2563EB", desc: "Organize os cadastros e as permissões de acesso." },
  cadastro_facial: { titulo: "Cadastro facial", modulo: "Gestão", icone: "rosto", iconBg: "#EDE9FE", iconColor: "#6D28D9", desc: "Anexe o termo do responsável e cadastre a foto de referência de cada atleta." },
  cadastro_atletas: { titulo: "Atletas", modulo: "Gestão", icone: "usuarios", iconBg: "#D1FAE5", iconColor: "#059669", desc: "O cadastro de cada atleta num lugar só: retrato, gênero, CPF, nascimento e modalidade." },
  plan_visao: { titulo: "Visão geral", modulo: "Planejamento", icone: "grade", iconBg: "#EDE9FE", iconColor: "#6D28D9", desc: "O estado do trimestre numa olhada: o que já foi entregue e o que ainda falta." },
  plan_ciclos: { titulo: "Ciclos e períodos", modulo: "Planejamento", icone: "calendario", iconBg: "#EDE9FE", iconColor: "#6D28D9", desc: "Abra o trimestre com o prazo de entrega e a janela de avaliações e testes." },
  plan_avaliacao: { titulo: "Avaliação do ciclo", modulo: "Planejamento", icone: "prancheta", iconBg: "#EDE9FE", iconColor: "#6D28D9", desc: "O que os atletas responderam na avaliação de encerramento do ciclo." },
  plan_tecnico: { titulo: "Planejamento técnico", modulo: "Planejamento", icone: "prancheta", iconBg: "#EDE9FE", iconColor: "#6D28D9", desc: "Os planos técnicos do trimestre, por modalidade." },
  plan_fisico: { titulo: "Preparação física", modulo: "Planejamento", icone: "halter", iconBg: "#EDE9FE", iconColor: "#6D28D9", desc: "Os planos de preparação física do trimestre, por modalidade." },
  plan_sessoes: { titulo: "Sessões de treino", modulo: "Planejamento", icone: "relogio", iconBg: "#EDE9FE", iconColor: "#6D28D9", desc: "As planilhas de treino escritas pela preparação física, exercício por exercício. A coordenação lê, não edita." },
  plan_carga: { titulo: "Carga e PSE", modulo: "Planejamento", icone: "pulso", iconBg: "#EDE9FE", iconColor: "#6D28D9", desc: "Carga de treino e percepção subjetiva de esforço, sessão a sessão.", badge: "Em breve" },
  resultados: { titulo: "Competições e resultados", modulo: "Desempenho", icone: "trofeu", iconBg: "#FEF3C7", iconColor: "#D97706", desc: "Lance a colocação de cada atleta e acompanhe o quadro de medalhas da modalidade." },
  plan_aprovacoes: { titulo: "Aprovações", modulo: "Planejamento", icone: "check", iconBg: "#EDE9FE", iconColor: "#6D28D9", desc: "O que está esperando decisão da coordenação." },
  datas: { titulo: "Dispensas e datas especiais", modulo: "Frequência", icone: "calendario", iconBg: "#EDE9FE", iconColor: "#7C3AED", desc: "Registre situações que alteram a frequência esperada dos atletas." },
  desempenho: { titulo: "Painel de desempenho", modulo: "Desempenho", icone: "trofeu", iconBg: "#FEF3C7", iconColor: "#D97706", desc: "Acompanhe os resultados físicos, técnicos e a evolução dos atletas do programa.", badge: "Em breve" },
  ficha: { titulo: "Ficha do atleta", modulo: "Desempenho", icone: "prancheta", iconBg: "#FEF3C7", iconColor: "#D97706", desc: "O desenvolvimento do atleta em uma só tela: cadastro, avaliações físicas e técnicas, testes, competições e rankings." },
  desemp_classificacao: { titulo: "Classificação geral", modulo: "Desempenho", icone: "medalha", iconBg: "#FEF3C7", iconColor: "#D97706", desc: "A posição de cada atleta no conjunto das avaliações da modalidade.", badge: "Em breve" },
  desemp_fisica: { titulo: "Avaliação física", modulo: "Desempenho", icone: "regua", iconBg: "#FEF3C7", iconColor: "#D97706", desc: "Antropometria, composição corporal, somatotipo e maturação." },
  desemp_tecnica: { titulo: "Avaliação técnica", modulo: "Desempenho", icone: "alvo", iconBg: "#FEF3C7", iconColor: "#D97706", desc: "Habilidades e critérios específicos definidos para cada modalidade." },
  desemp_testes: { titulo: "Testes físicos", modulo: "Desempenho", icone: "relogio", iconBg: "#FEF3C7", iconColor: "#D97706", desc: "Impulsão, abdominal, circuito, flexibilidade e tempo de pista, com as capacidades por nota." },
  desemp_evolucao: { titulo: "Evolução", modulo: "Desempenho", icone: "barras", iconBg: "#FEF3C7", iconColor: "#D97706", desc: "Comparação entre avaliações, pontos fortes e necessidades de desenvolvimento.", badge: "Em breve" },
  comparativo: { titulo: "Comparativo de frequência", modulo: "Frequência", icone: "linha", iconBg: "#E0F2FE", iconColor: "#0891B2", desc: "Compare a frequência dos atletas, modalidades e núcleos ao longo do período." },
  presenca: { titulo: "Presença diária", modulo: "Frequência", icone: "calendarioCheck", iconBg: "#FEF9C3", iconColor: "#CA8A04", desc: "Veja quem registrou presença em cada dia de treino, por modalidade." },
  presenca_fisica: { titulo: "Presença", modulo: "Frequência", icone: "calendarioCheck", iconBg: "#FEF9C3", iconColor: "#CA8A04", desc: "Presença dos atletas em cada dia: treino diário na linha de cima, treino físico na de baixo." },
  solicitacoes: { titulo: "Solicitações de frequência", modulo: "Frequência", icone: "envelope", iconBg: "#FEF3C7", iconColor: "#D97706", desc: "Analise os pedidos de correção de falta enviados pelos atletas." },
  relatorio: { titulo: "Relatórios", modulo: "Frequência", icone: "barras", iconBg: "#D1FAE5", iconColor: "#059669", desc: "Gere, consulte e exporte os registros de frequência do programa." },
  treinos_externos: { titulo: "Treinos externos", modulo: "Frequência", icone: "pino", iconBg: "#FFE4E6", iconColor: "#BE123C", desc: "Treinos fora dos locais oficiais, cadastrados pela equipe técnica. Registros nesses locais e datas não geram aviso." },
};

// ── APP ───────────────────────────────────────────────────────
// O provider fica por fora para que qualquer tela possa traduzir o
// identificador do núcleo no nome de exibição.
export default function App() {
  // A ficha de cadastro de núcleo fica FORA do login, e por isso é
  // decidida aqui, antes de qualquer coisa do painel. Quem preenche a
  // parte técnica é o coordenador técnico de um núcleo que ainda não
  // existe, e por isso ainda não tem conta: o token do link é a
  // credencial, conferida no servidor.
  //
  // Fora do NucleosProvider de propósito: a página não traduz
  // identificador de núcleo, e o provider leria a lista de núcleos sem
  // nenhum uso.
  const daFicha = typeof window !== "undefined"
    ? dadosDoLinkDaFicha(window.location.search)
    : { fichaId: "", token: "" };
  const naFicha = typeof window !== "undefined"
    && window.location.pathname.replace(/\/+$/, "") === "/ficha";

  if (naFicha && daFicha.fichaId && daFicha.token) {
    return <FichaNucleo fichaId={daFicha.fichaId} token={daFicha.token} />;
  }

  return (
    <NucleosProvider>
      <Painel />
    </NucleosProvider>
  );
}

function Painel() {
  const isMobile = useIsMobile();
  const nomeNucleo = useNomeNucleo();
  const [authCarregando, setAuthCarregando] = useState(true);
  const [user, setUser] = useState(null);
  const [userNucleo, setUserNucleo] = useState(null);
  const [listaNucleos, setListaNucleos] = useState([]); // p/ seletor global (Coord Geral/Admin)
  const [userModalidade, setUserModalidade] = useState(null); // usada só pelo nível "tecnico"
  const [userNome, setUserNome] = useState(null);
  // Data de nascimento: o painel não mostra idade em tela nenhuma da
  // coordenação, mas a avaliação do ciclo grava a idade de quem
  // respondeu, e ela é calculada daqui.
  const [userNascimento, setUserNascimento] = useState(null);
  const [nivelAcesso, setNivelAcesso] = useState(null); // admin | coordenador_geral | coordenador_tecnico | tecnico
  const [senhaAlterada, setSenhaAlterada] = useState(true); // esconde a faixa por padrão até saber
  const [mostrarTrocaSenha, setMostrarTrocaSenha] = useState(false);
  const [mostrarMenuPerfil, setMostrarMenuPerfil] = useState(false);
  const [avisoSenha, setAvisoSenha] = useState(null);
  const [erroLogin, setErroLogin] = useState(null);
  const [entrando, setEntrando] = useState(false);

  const [aba, setAba] = useState("inicio");
  const [pendentesSolic, setPendentesSolic] = useState(0);
  const [modulosAbertos, setModulosAbertos] = useState({ gestao: true, frequencia: true, planejamento: true, desempenho: false });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      try {
        if (!fbUser) {
          setUser(null); setUserNucleo(null); setUserModalidade(null); setUserNome(null); setUserNascimento(null); setNivelAcesso(null);
          return;
        }
        const snap = await getDoc(doc(db, "users", fbUser.uid));
        if (!snap.exists()) {
          setErroLogin("Usuário sem cadastro no sistema. Procure a coordenação.");
          await signOut(auth);
          return;
        }
        const data = snap.data();
        if (data.ativo === false) {
          setErroLogin("Acesso desativado. Procure a coordenação.");
          await signOut(auth);
          return;
        }
        const nivel = classificarAcesso(data.perfil);
        if (nivel === "sem_acesso") {
          setErroLogin("Este painel é exclusivo para coordenadores e técnicos.");
          await signOut(auth);
          return;
        }
        setUser(fbUser);
        setUserNucleo(data.nucleo || null);
        setUserModalidade(data.modalidade || null);
        setUserNome(data.name || fbUser.email);
        setUserNascimento(data.dataNascimento || null);
        setNivelAcesso(nivel);
        // Faixa de recomendação aparece só se o usuário ainda não trocou a senha padrão.
        setSenhaAlterada(data.senhaAlterada === true);
        setErroLogin(null);
      } catch (e) {
        setErroLogin("Erro ao validar acesso: " + (e.message || ""));
      } finally {
        setAuthCarregando(false);
      }
    });
    return () => unsub();
  }, []);

  async function entrar(email, senha) {
    setEntrando(true); setErroLogin(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), senha);
      // o onAuthStateChanged acima cuida da validação do doc users/{uid}
    } catch {
      setErroLogin("E-mail ou senha inválidos.");
    } finally {
      setEntrando(false);
    }
  }

  async function sair() {
    await signOut(auth);
    setUser(null); setUserNucleo(null); setUserModalidade(null); setUserNome(null); setUserNascimento(null); setNivelAcesso(null);
  }

  // Contagem de solicitações pendentes do núcleo (para o badge no menu).
  // Recarrega ao trocar de aba — assim o número cai logo após resolver uma.
  //
  // Conta no servidor em vez de baixar a coleção: o selo mostra um número, e
  // baixava as 95 solicitações do núcleo a CADA troca de aba para chegar
  // nele. O count() cobra uma leitura por até mil documentos.
  useEffect(() => {
    if (!userNucleo || !["admin", "coordenador_geral", "coordenador", "coordenador_tecnico"].includes(nivelAcesso)) {
      setPendentesSolic(0);
      return;
    }
    let ativo = true;
    (async () => {
      try {
        // O selo soma as duas coisas que esperam a coordenação: o pedido
        // que ninguém decidiu e a decisão do técnico que ela ainda não viu.
        // São trabalhos diferentes, e por isso aparecem separados DENTRO da
        // tela; no menu, o que importa é que há algo esperando.
        const [quantas, paraCiencia] = await Promise.all([
          contarSolicitacoesPendentes(userNucleo),
          carregarDecisoesParaCiencia(userNucleo).catch(() => []),
        ]);
        if (ativo) setPendentesSolic(quantas + paraCiencia.length);
      } catch { if (ativo) setPendentesSolic(0); }
    })();
    return () => { ativo = false; };
  }, [userNucleo, nivelAcesso, aba]);

  // Carrega a lista de núcleos para o seletor global — só para quem enxerga
  // todos os núcleos (Administrador Geral e Coordenador Geral). Os demais
  // ficam presos ao próprio núcleo (não precisam da lista).
  useEffect(() => {
    const vejoTodos = nivelAcesso === "admin" || nivelAcesso === "coordenador_geral";
    if (!vejoTodos) { setListaNucleos([]); return; }
    let ativo = true;
    (async () => {
      try {
        const lista = await carregarNucleos();
        const ativos = lista.filter(n => n.ativo !== false);
        if (ativo) {
          setListaNucleos(ativos);
          // Se o núcleo atual não está na lista (ou é nulo), começa no primeiro.
          setUserNucleo(prev => {
            const ids = ativos.map(n => n.id);
            return (prev && ids.includes(prev)) ? prev : (ids[0] || prev);
          });
        }
      } catch { if (ativo) setListaNucleos([]); }
    })();
    return () => { ativo = false; };
  }, [nivelAcesso]);

  if (authCarregando) return <TelaCarregando />;
  if (!user) return <TelaLogin onEntrar={entrar} erro={erroLogin} carregando={entrando} />;

  const isAdmin = nivelAcesso === "admin";
  const isCoordGeral = nivelAcesso === "coordenador_geral";
  const isTecnico = nivelAcesso === "tecnico";
  const isAuxiliar = nivelAcesso === "auxiliar";
  const isPreparador = nivelAcesso === "preparador";
  const isAtleta = nivelAcesso === "atleta";
  // Adm Geral e Coordenador Geral enxergam/escolhem qualquer núcleo.
  const vejoTodosNucleos = isAdmin || isCoordGeral;
  // Técnico, auxiliar, preparador e atleta usam tela única (sem barra
  // lateral nem abas) — só acompanham, não gerenciam.
  const semMenu = isTecnico || isAuxiliar || isPreparador || isAtleta;

  const ROTULO_NIVEL = {
    admin: "Admin",
    coordenador_geral: "Coordenador Geral",
    coordenador: "Coordenador",
    coordenador_tecnico: "Coordenador Técnico",
    tecnico: "Técnico",
  };

  const rotuloTexto = isAdmin
    ? "Admin · todos os núcleos"
    : isCoordGeral
      ? "Coordenador Geral · todos os núcleos"
      : isAtleta
        ? `Atleta · ${userModalidade || "—"}`
        : isTecnico
          ? `Técnico · ${userModalidade || "—"} · ${nomeNucleo(userNucleo) || "—"}`
          : isAuxiliar
            ? `Auxiliar · ${userModalidade || "—"} · ${nomeNucleo(userNucleo) || "—"}`
            : isPreparador
              ? `Preparação Física · ${nomeNucleo(userNucleo) || "—"}`
              : `${ROTULO_NIVEL[nivelAcesso] || "Coordenador"} · ${nomeNucleo(userNucleo) || "—"}`;

  // Nome da área conforme o perfil — aparece ao lado da logo no cabeçalho.
  const tituloArea = isAdmin
    ? "Administração Geral"
    : isCoordGeral
      ? "Coordenação Geral"
      : isAtleta
        ? "Área do Atleta"
        : isTecnico
          ? "Área Técnica"
          : isAuxiliar
            ? "Área do Auxiliar"
            : isPreparador
              ? "Preparação Física"
              : "Portal de Gestão";

  // Menu organizado em MÓDULOS (accordion — clica no módulo para abrir/
  // fechar seus itens). Só as abas permitidas para o perfil aparecem.
  // "Núcleos" aparece para quem cria núcleos: Adm Geral e Coordenador Geral.
  const MODULOS = [
    { id: "inicio", titulo: "", solto: true, abas: ["inicio"] },
    // Administração é só do administrador, e é onde a abertura de núcleo
    // acontece de ponta a ponta: a ficha que a coordenação preenche por
    // link, a conferência do que chegou e a criação do núcleo. Núcleos
    // mora aqui para ele, porque criar e apagar são dele; para o
    // coordenador geral a mesma aba continua em Gestão, onde ele mantém
    // modalidades, categorias e locais dos núcleos que existem.
    ...(isAdmin ? [{ id: "administracao", titulo: "Administração", abas: ["painel_geral", "fichas", "nucleos"] }] : []),
    // "Atletas" vem logo depois de "Gerenciar usuários" porque é a leitura do
    // que se preenche lá. Gerenciar usuários cuida de perfil, acesso e
    // permissão, de todo mundo; esta é só dos atletas, e é a lista que a
    // coordenação mantinha num documento à parte.
    { id: "gestao", titulo: "Gestão", abas: isCoordGeral
      ? ["nucleos", "atletas", "cadastro_atletas", "cadastro_facial"]
      : ["atletas", "cadastro_atletas", "cadastro_facial"] },
    // Frequência na ordem em que se usa: o dia a dia primeiro, depois o que
    // muda o esperado, e por último o que se lê e se exporta.
    { id: "frequencia", titulo: "Frequência",
      abas: ["presenca_fisica", "solicitacoes", "treinos_externos", "datas", "relatorio", "comparativo"] },
    // Planejamento saiu de dentro de Frequência: é o que se pretende fazer no
    // trimestre, e ninguém abre o menu de presença procurando por isso.
    { id: "planejamento", titulo: "Planejamento",
      abas: ["plan_visao", "plan_ciclos", "plan_avaliacao", "plan_tecnico", "plan_fisico", "plan_sessoes", "plan_carga", "plan_aprovacoes"] },
    // O módulo inteiro para toda a coordenação. Esconder área em construção
    // de um perfil e mostrar para outro não protege nada, e faz duas pessoas
    // que trabalham juntas verem menus diferentes.
    // A ficha abre o módulo porque é a leitura, e as outras abas são o
    // lançamento: quem entra em Desempenho quer saber como um atleta está, e
    // só depois disso vai medir ou lançar.
    //
    // Competições e resultados saiu de Planejamento em 15/10/2026. Lá ela
    // estava pelo lado do calendário, que é quando a competição é
    // compromisso futuro; mas o que se faz na tela é lançar colocação e
    // ler o quadro de medalhas, que é desempenho acontecido. Quem procura
    // o resultado de um atleta procura junto com a avaliação dele.
    { id: "desempenho", titulo: "Desempenho", nota: "em desenvolvimento",
      abas: ["ficha", "desempenho", "desemp_fisica", "desemp_tecnica", "desemp_testes", "resultados", "desemp_classificacao", "desemp_evolucao"] },
  ];

  // Item do menu. O ícone não tem mais caixinha colorida atrás: sobre o
  // fundo escuro, vinte caixinhas pastel viravam um mosaico e o item ativo
  // deixava de se destacar — que é a única coisa que o menu precisa dizer.
  function ItemMenu({ id, ativo, onClick }) {
    const m = METADADOS_ABA[id];
    const badge = id === "solicitacoes" && pendentesSolic > 0 ? pendentesSolic : null;
    return (
      <button onClick={onClick} title={m.titulo} style={{
        display: "flex", alignItems: "center", gap: 11, width: isMobile ? "auto" : "100%",
        textAlign: "left", border: "none", font: "inherit",
        background: ativo ? MENU.itemAtivo : "transparent",
        color: ativo ? BRANCO : MENU.item,
        borderRadius: 9, padding: isMobile ? "10px 13px" : "10px 12px",
        cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
        transition: "background 0.12s",
      }}>
        <Icone nome={m.icone} tamanho={18} />
        <span style={{ fontSize: 13.5, fontWeight: ativo ? 700 : 500 }}>{m.titulo}</span>
        {badge && (
          <span style={{ marginLeft: "auto", background: PERIGO, color: "#fff", fontSize: 11, fontWeight: 800, minWidth: 20, height: 20, borderRadius: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "0 6px" }}>{badge}</span>
        )}
      </button>
    );
  }

  const metaAtual = METADADOS_ABA[aba];

  return (
    <div style={{ fontFamily: FONTE, background: "#F4F7FB", minHeight: "100vh" }}>
      {/* Cabeçalho: logo à esquerda, menu de perfil à direita */}
      <div style={{ background: BRANCO, borderBottom: "1px solid #E8EDF3", padding: isMobile ? "10px 12px" : "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src="/logo-revelar-horizontal.png" alt="Revelar Talentos" style={{ height: isMobile ? 32 : 40, objectFit: "contain" }} />
          {!isMobile && (
            <div style={{ borderLeft: "1.5px solid #E2E8F0", paddingLeft: 14 }}>
              <div style={{ fontSize: 17, color: TEXTO, fontWeight: 800, lineHeight: 1.2, letterSpacing: -0.3 }}>{tituloArea}</div>
              <div style={{ fontSize: 12, color: CINZA_ESC, fontWeight: 500, marginTop: 1 }}>Revelar Talentos</div>
            </div>
          )}
        </div>

        <div style={{ position: "relative" }}>
          <button onClick={() => setMostrarMenuPerfil(v => !v)} style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "1px solid #E5E7EB", borderRadius: 10, padding: "6px 10px", cursor: "pointer" }}>
            <span style={{ width: 32, height: 32, borderRadius: "50%", background: AZUL, color: BRANCO, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
              {iniciaisDoNome(userNome)}
            </span>
            {!isMobile && (
              <span style={{ textAlign: "left", lineHeight: 1.25 }}>
                <span style={{ display: "block", fontSize: 13, fontWeight: 700, color: TEXTO }}>{userNome}</span>
                <span style={{ display: "block", fontSize: 11, color: CINZA_ESC }}>{rotuloTexto}</span>
              </span>
            )}
            <span style={{ fontSize: 12, color: CINZA_ESC }}>▾</span>
          </button>

          {mostrarMenuPerfil && (
            <>
              <div onClick={() => setMostrarMenuPerfil(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div style={{ position: "absolute", top: "110%", right: 0, zIndex: 50, background: BRANCO, border: "1px solid #E5E7EB", borderRadius: 10, minWidth: 210, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                <div style={{ padding: "12px 14px", borderBottom: "1px solid #F1F5F9" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: TEXTO }}>{userNome}</div>
                  <div style={{ fontSize: 11, color: CINZA_ESC, marginTop: 2 }}>{rotuloTexto}</div>
                </div>
                <button onClick={() => { setMostrarTrocaSenha(true); setAvisoSenha(null); setMostrarMenuPerfil(false); }} style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left", background: "none", border: "none", padding: "11px 14px", fontSize: 13, color: TEXTO, cursor: "pointer", font: "inherit" }}>
                  <Icone nome="chave" tamanho={16} /> Alterar senha
                </button>
                <a href={LINK_APP} target="_blank" rel="noopener noreferrer" onClick={() => setMostrarMenuPerfil(false)} style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left", background: "none", border: "none", borderTop: "1px solid #F1F5F9", padding: "11px 14px", fontSize: 13, color: TEXTO, cursor: "pointer", textDecoration: "none", boxSizing: "border-box" }}>
                  <Icone nome="celular" tamanho={16} /> Baixar app (Android)
                </a>
                <button onClick={() => { sair(); setMostrarMenuPerfil(false); }} style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left", background: "none", border: "none", borderTop: "1px solid #F1F5F9", padding: "11px 14px", fontSize: 13, color: PERIGO, cursor: "pointer", font: "inherit" }}>
                  <Icone nome="sair" tamanho={16} /> Sair
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row" }}>
        {/* Barra lateral — técnico não tem, vai direto pra tela própria */}
        {!semMenu && (
          <div style={{
            width: isMobile ? "auto" : 248, flexShrink: 0,
            background: MENU.fundo,
            borderRight: "none", borderBottom: "none",
            padding: isMobile ? "8px 8px" : "14px 12px 32px",
            display: "flex", flexDirection: isMobile ? "row" : "column", gap: 2,
            overflowX: isMobile ? "auto" : "visible",
            minHeight: isMobile ? 0 : "calc(100vh - 66px)",
          }}>
            {MODULOS.map(mod => {
              const abasDoModulo = mod.abas.filter(a => METADADOS_ABA[a]);
              if (abasDoModulo.length === 0) return null;
              const aberto = mod.solto || isMobile ? true : modulosAbertos[mod.id];
              return (
                <div key={mod.id} style={{ marginBottom: isMobile ? 0 : 2 }}>
                  {!isMobile && !mod.solto && (
                    <button
                      onClick={() => setModulosAbertos(s => ({ ...s, [mod.id]: !s[mod.id] }))}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: "none", cursor: "pointer", padding: "14px 12px 7px", borderRadius: 8, font: "inherit" }}
                    >
                      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4, flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: MENU.grupo, textTransform: "uppercase", letterSpacing: 0.9, textAlign: "left" }}>{mod.titulo}</span>
                        {mod.nota && (
                          <span style={{ fontSize: 9.5, fontWeight: 700, color: "#FDE68A", background: "rgba(253, 224, 71, 0.14)", borderRadius: 20, padding: "2px 7px", whiteSpace: "nowrap", textTransform: "none", letterSpacing: 0 }}>
                            {mod.nota}
                          </span>
                        )}
                      </span>
                      <span style={{ color: MENU.grupo, transform: aberto ? "none" : "rotate(-90deg)", transition: "transform 0.15s", display: "flex" }}>
                        <Icone nome="chevronBaixo" tamanho={15} />
                      </span>
                    </button>
                  )}
                  {aberto && abasDoModulo.map(id => (
                    <ItemMenu key={id} id={id} ativo={aba === id} onClick={() => setAba(id)} />
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Conteúdo */}
        <div style={{ flex: 1, minWidth: 0, padding: isMobile ? "20px 16px 48px" : "28px 32px 48px" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            {!semMenu && metaAtual && (
              <div style={{ marginBottom: 24 }}>
                {/* Trilha: o módulo e a página. Quem chega por um atalho do
                    Início precisa saber em que parte do sistema caiu. */}
                {metaAtual.modulo && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, color: CINZA_ESC, marginBottom: 8, ...TIPO_PAGINA.migalha }}>
                    <span>{metaAtual.modulo}</span>
                    <Icone nome="chevronDireita" tamanho={13} />
                    <span style={{ color: TEXTO }}>{metaAtual.titulo}</span>
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <h1 style={{ ...TIPO_PAGINA.titulo, color: TEXTO, margin: 0 }}>{metaAtual.titulo}</h1>
                  {metaAtual.badge && (
                    <span style={{ background: AZUL, color: BRANCO, fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20 }}>
                      {metaAtual.badge.toUpperCase()}
                    </span>
                  )}
                </div>
                <div style={{ color: CINZA_ESC, marginTop: 6, ...TIPO_PAGINA.sub }}>{metaAtual.desc}</div>
              </div>
            )}

            {/* Seletor de NÚCLEO global — só para quem vê todos os núcleos
                (Administrador Geral e Coordenador Geral). Controla qual núcleo
                é exibido em TODAS as seções (relatórios, presença, usuários,
                solicitações). Sem isto, esses perfis ficavam presos ao próprio
                núcleo (entravam direto no IFCE). */}
            {!semMenu && vejoTodosNucleos && listaNucleos.length > 0 && aba !== "nucleos" && (
              <div style={{ marginBottom: 18, background: BRANCO, border: `1px solid ${BORDA}`, borderRadius: RAIO.lg, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: CINZA_ESC, textTransform: "uppercase", letterSpacing: 0.6 }}>Núcleo</span>
                  <select
                    value={userNucleo || ""}
                    onChange={e => setUserNucleo(e.target.value)}
                    style={{ fontSize: 14, fontWeight: 600, color: TEXTO, border: `1px solid ${ACAO}`, borderRadius: RAIO.md, padding: "9px 12px", background: BRANCO, cursor: "pointer", minWidth: 220, fontFamily: "inherit" }}
                  >
                    {listaNucleos.map(n => (
                      <option key={n.id} value={n.id}>{n.nome || n.id}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {!senhaAlterada && (
              <div style={{ background: ALERTA_CL, border: `1px solid ${ALERTA}44`, borderRadius: 10, padding: "12px 16px", marginBottom: 16, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontSize: 20 }}>🔑</div>
                <div style={{ flex: 1, minWidth: 200, fontSize: 13, color: "#92400E", fontWeight: 500 }}>
                  Você está usando a senha padrão. Recomendamos trocá-la por uma senha só sua. A senha é a mesma para o portal e para o aplicativo, então ao alterá-la aqui ela muda nos dois.
                </div>
                <Btn onClick={() => { setMostrarTrocaSenha(true); setAvisoSenha(null); }} style={{ background: ALERTA, fontSize: 13, padding: "8px 16px" }}>
                  Alterar agora
                </Btn>
              </div>
            )}
            {avisoSenha && <div style={{ marginBottom: 16 }}><StatusBox tipo="ok" msg={avisoSenha} /></div>}

            {semMenu ? (
              isPreparador ? (
                <TelaPreparador uid={user.uid} userNucleo={userNucleo} podeConfigurar={true} />
              ) : isTecnico || isAuxiliar ? (
                // A área do técnico e do auxiliar voltou, com o planejamento
                // trimestral pronto e a decisão de justificativa nas mãos de
                // quem tem a informação.
                //
                // podeCorrigir separa os dois: o técnico decide a
                // justificativa da modalidade dele, o auxiliar consulta o que
                // já foi abonado. semComparativo tira do auxiliar a leitura
                // entre modalidades, que é de gestão.
                <TelaTecnico uid={user.uid} userNucleo={userNucleo} modalidade={userModalidade}
                  podeCorrigir={isTecnico} semComparativo={isAuxiliar} />
              ) : (
                // A Área do Atleta abre depois da avaliação do ciclo. Quem
                // já respondeu nem vê o portão: ele pergunta por um
                // documento de id previsível e passa adiante.
                <PortaDaAvaliacao
                  uid={user.uid} nome={userNome} email={user.email}
                  nucleo={userNucleo} modalidade={userModalidade}
                  dataNascimento={userNascimento}
                >
                  <SecaoAtleta uid={user.uid} userNucleo={userNucleo} modalidade={userModalidade} userNome={userNome} />
                </PortaDaAvaliacao>
              )
            ) : (
              <>
                {aba === "inicio" && (
                  <SecaoInicio
                    userNucleo={userNucleo}
                    userNome={userNome}
                    onIr={(destino) => setAba(destino)}
                  />
                )}
                {aba === "relatorio" && <Resumo nucleo={userNucleo} isAdmin={vejoTodosNucleos} />}
                {aba === "relatorio" && <SecaoRelatorio userNucleo={userNucleo} isAdmin={vejoTodosNucleos} nivelAcesso={nivelAcesso} />}
                {aba === "comparativo" && <SecaoComparativo userNucleo={userNucleo} isAdmin={vejoTodosNucleos} />}
                {aba === "presenca_fisica" && <SecaoPresencaFisica userNucleo={userNucleo} podeMarcar={false} />}
                {aba === "datas" && <SecaoDatas userNucleo={userNucleo} isAdmin={vejoTodosNucleos} />}
                {aba === "solicitacoes" && <SecaoSolicitacoes userNucleo={userNucleo} />}
                {aba === "treinos_externos" && <SecaoTreinosExternos userNucleo={userNucleo} modalidade="" ehCoordenacao={true} />}
                {aba === "atletas" && <SecaoAtletas userNucleo={userNucleo} isAdmin={isAdmin} nivelAcesso={nivelAcesso} />}
                {aba === "cadastro_facial" && <SecaoCadastroFacial userNucleo={userNucleo} />}
                {aba === "cadastro_atletas" && <SecaoCadastroAtletas userNucleo={userNucleo} />}
                {aba === "plan_visao" && <SecaoPlanejamentoCoord userNucleo={userNucleo} secao="visao" />}
                {aba === "plan_ciclos" && <SecaoPlanejamentoCoord userNucleo={userNucleo} secao="ciclos" />}
                {/* A planilha é a MESMA tela da preparação física, em modo de
                    leitura. Uma segunda tela só para a coordenação ver o
                    mesmo treino divergiria da original na primeira coluna
                    que a preparação física acrescentasse. */}
                {aba === "plan_sessoes" && <SecaoPlanilhaTreino userNucleo={userNucleo} podeEditar={false} />}
                {aba === "plan_avaliacao" && <SecaoAvaliacaoCoord userNucleo={userNucleo} />}
                {aba === "plan_tecnico" && <SecaoPlanejamentoCoord userNucleo={userNucleo} secao="tecnico" />}
                {aba === "plan_fisico" && <SecaoPlanejamentoCoord userNucleo={userNucleo} secao="fisico" />}
                {aba === "plan_aprovacoes" && <SecaoPlanejamentoCoord userNucleo={userNucleo} secao="aprovacoes" />}
                {aba === "resultados" && (
                  <SecaoResultados nucleo={userNucleo} nucleoNome={nomeNucleo(userNucleo)} />
                )}
                {aba === "desemp_ranking" && <SecaoRanking userNucleo={userNucleo} />}
                {aba === "desempenho" && <SecaoDesempenho />}
                {aba === "ficha" && <SecaoFichaAtleta userNucleo={userNucleo} />}
                {aba === "desemp_fisica" && <SecaoAvaliacaoFisica userNucleo={userNucleo} />}
                {aba === "desemp_tecnica" && <SecaoAvaliacaoTecnica userNucleo={userNucleo} />}
                {aba === "desemp_testes" && <SecaoTestesFisicos userNucleo={userNucleo} />}
                {["desemp_classificacao",
                  "desemp_evolucao", "plan_carga"].includes(aba) && (
                  <TelaEmBreve meta={METADADOS_ABA[aba]} />
                )}
                {aba === "nucleos" && vejoTodosNucleos && <SecaoNucleos isAdmin={isAdmin} />}
                {aba === "painel_geral" && isAdmin && <SecaoPainelGeral userNucleo={userNucleo} />}
                {aba === "fichas" && isAdmin && <SecaoFichasNucleo />}
              </>
            )}
          </div>
        </div>
      </div>

      {mostrarTrocaSenha && (
        <ModalAlterarSenha
          onFechar={() => setMostrarTrocaSenha(false)}
          onSucesso={() => {
            setMostrarTrocaSenha(false);
            setSenhaAlterada(true);
            setAvisoSenha("Senha alterada com sucesso.");
          }}
        />
      )}

    </div>
  );
}

// ── Tela do Técnico: Meus Atletas + Comparativo (só da modalidade dele) ──
// Mini-menu de 2 opções. "Meus Atletas" é a SecaoTecnico de sempre;
// "Comparativo" é a SecaoComparativo travada na modalidade do técnico
// (item novo — o técnico só compara a própria modalidade).
// NOTA: a solicitação de correção pelo técnico está DESLIGADA por ora
// (podeCorrigir=false por padrão). Para religar, volte o padrão para true.
// Tela de descrição de um subitem de Desempenho (módulo ainda não
// desenvolvido). Mostra o que a área vai conter, com visual de "em breve".
function TelaEmBreve({ meta }) {
  if (!meta) return null;
  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ background: BRANCO, border: `1px solid #E5E7EB`, borderRadius: 14, padding: "28px 26px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
          <div style={{ width: 52, height: 52, borderRadius: 12, background: meta.iconBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>{meta.icone}</div>
          <div>
            <div style={{ fontSize: 19, fontWeight: 800, color: TEXTO }}>{meta.titulo}</div>
            <span style={{ display: "inline-block", marginTop: 4, background: "#FEF3C7", color: "#92400E", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>EM BREVE</span>
          </div>
        </div>
        <p style={{ fontSize: 14.5, color: CINZA_ESC, lineHeight: 1.7, margin: 0 }}>{meta.desc}</p>
        <div style={{ marginTop: 20, padding: "14px 16px", background: "#F9FAFB", border: "1px dashed #D1D5DB", borderRadius: 10, fontSize: 13, color: CINZA_ESC, lineHeight: 1.6 }}>
          Esta parte do sistema ainda está sendo construída. O que já existe continua
          funcionando normalmente nas outras abas, e esta é liberada assim que estiver pronta.
        </div>
      </div>
    </div>
  );
}

